import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Trash01 } from "untitledui-js/react";

import { Typography } from "@pondigitalsolutions/pon-component-library";
import { Notification } from "@hooks/useGetNotifications";
import { useUpdateNotificationByIds } from "@hooks/useUpdateNotificationByIds";
import { Label } from "./Label";
import { PageBody } from "../storyblok/PageBody";
import { IconButton } from "./IconButton";
import { showNotificationTimeSince } from "@utils/showNotificationTimeSince";

interface NotificationDetailsProps {
  notificationDetails: Notification;
  setNotificationDetails: React.Dispatch<
    React.SetStateAction<Notification | null>
  >;
}

export const NotificationDetails = ({
  notificationDetails,
  setNotificationDetails,
}: NotificationDetailsProps) => {
  const { t } = useTranslation();
  const { mutate: updateNotificationByIds } = useUpdateNotificationByIds();

  const notificationTimeSince = showNotificationTimeSince(
    notificationDetails?.createdAt,
    t
  );

  useEffect(() => {
    // Mark the notification as read when the details are viewed
    updateNotificationByIds([
      {
        id: notificationDetails.id,
        isRead: true,
        isDeleted: false,
      },
    ]);
  }, [notificationDetails.id, updateNotificationByIds]);

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-4 md:min-w-[500px]">
        <div className="flex items-center gap-2">
          <IconButton
            id="notifications-back"
            onClick={() => setNotificationDetails(null)}
            ariaLabel={t("notifications.back")}
            icon={<ChevronLeft width={14} />}
            variant="sm"
          />
          <Typography as="h6" className="text-lg font-bold text-white">
            {t("notifications.allNotifications")}
          </Typography>
        </div>
        <IconButton
          id="markNotificationAsDeleted"
          onClick={() => {
            updateNotificationByIds([
              {
                id: notificationDetails.id,
                isDeleted: true,
                isRead: true,
              },
            ]);
            setNotificationDetails(null);
          }}
          ariaLabel={t("notifications.markAsDeleted")}
          icon={<Trash01 width={14} />}
          variant="sm"
        />
      </div>
      <div className="flex flex-col gap-3.5 rounded-2xl">
        <Typography
          as="h6"
          className="text-xl leading-[125%] font-semibold text-white"
        >
          {notificationDetails.title}
        </Typography>
        {notificationDetails.category && (
          <Label variant="soft" className="w-fit">
            {notificationDetails.category}
          </Label>
        )}
        {notificationDetails.body && (
          <PageBody blok={notificationDetails.body} className="notifications" />
        )}
      </div>
      <Typography className="mt-4 text-xs font-semibold text-white/80">
        {notificationTimeSince}
      </Typography>
    </>
  );
};
